import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plane, Lock } from "lucide-react";
import api from "@/utils/api";

// Aug 2026 — Beleg-Verknüpfung: Hotel-/Bahnbelege einer Geschäftsreise zuordnen.
export const TripLinkSection = ({ expense }) => {
  const [trips, setTrips] = useState(null);
  const [current, setCurrent] = useState(expense?.travel_trip_id || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setCurrent(expense?.travel_trip_id || ""); }, [expense?.id, expense?.travel_trip_id]);

  useEffect(() => {
    let cancelled = false;
    api.get("/travel/trips")
      .then((r) => { if (!cancelled) setTrips(r.data); })
      .catch(() => { if (!cancelled) setTrips([]); });
    return () => { cancelled = true; };
  }, [expense?.id]);

  if (!expense?.id) return null;

  // Aus einer Reise erzeugte Pauschal-Buchung → nur Hinweis, keine Änderung möglich
  if (expense.travel_position) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-sm text-emerald-800"
           data-testid="expense-travel-generated-badge">
        <Lock className="h-4 w-4 shrink-0" />
        Aus einer Geschäftsreise erzeugte Pauschal-Buchung — Änderungen nur über die Reise
        (Finanzen → Reisen, „Wieder öffnen“).
      </div>
    );
  }

  if (!trips || trips.length === 0) return null;

  const change = async (val) => {
    const tripId = val === "none" ? "" : val;
    setBusy(true);
    try {
      if (tripId) {
        const res = await api.post(`/travel/trips/${tripId}/link-expense`, { expense_id: expense.id });
        if (res.data?.warning) toast.warning(res.data.warning, { duration: 9000 });
        else toast.success("Beleg der Reise zugeordnet");
      } else if (current) {
        await api.post(`/travel/trips/${current}/unlink-expense`, { expense_id: expense.id });
        toast.success("Zuordnung entfernt");
      }
      setCurrent(tripId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zuordnung fehlgeschlagen");
    }
    setBusy(false);
  };

  return (
    <div data-testid="expense-trip-link-section">
      <Label className="flex items-center gap-1.5">
        <Plane className="h-3.5 w-3.5 text-emerald-600" /> Geschäftsreise zuordnen
      </Label>
      <Select value={current || "none"} onValueChange={change} disabled={busy}>
        <SelectTrigger data-testid="expense-trip-link-select" className="mt-1">
          <SelectValue placeholder="Keine Reise" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Keine Reise</SelectItem>
          {trips.map((t) => (
            <SelectItem key={t.id} value={t.id} data-testid={`expense-trip-option-${t.id}`}>
              {t.title} · {t.destination_place} ({(t.start_at || "").slice(0, 10)})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="mt-1 text-xs text-slate-400">
        Verknüpfte Belege (Hotel, Bahn, Taxi …) erscheinen bei der Reise — so bleibt sichtbar,
        dass Pauschale und Beleg nicht doppelt angesetzt werden.
      </p>
    </div>
  );
};

export default TripLinkSection;
